import { ReactNode } from "react";
import { useFormState } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps } from "@mui/material/styles";

type SubmitButtonProps = {
  submitButtonLabel: ReactNode;
  formName: string;
  submitIsDisabled?: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  control?: any;
  sx?: SxProps;
  submitTooltip?: string;
  /** Set when the form supplies a blockers strip — see ModalForm's `blockers` prop. */
  hasBlockers?: boolean;
};

type SubmitButtonBaseProps = {
  submitButtonLabel: ReactNode;
  formName: string;
  isSubmitting: boolean;
  onSubmit: () => void;
  sx?: SxProps;
  disabled?: boolean;
  tooltipTitle?: string;
};

function SubmitButtonBase({
  submitButtonLabel,
  formName,
  isSubmitting,
  onSubmit,
  sx,
  disabled,
  tooltipTitle = ""
}: Readonly<SubmitButtonBaseProps>) {
  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <LoadingButton
          onClick={onSubmit}
          variant="contained"
          form={formName}
          disabled={disabled}
          loading={isSubmitting}
          sx={sx}
        >
          {submitButtonLabel}
        </LoadingButton>
      </span>
    </Tooltip>
  );
}

function FormStateSubmitButton(props: Readonly<SubmitButtonProps>) {
  const { control, submitIsDisabled, hasBlockers } = props;
  const { isValid, errors } = useFormState({ control });
  const errorsIsEmpty = Object.values(errors).length === 0;

  const errorMessages = Object.values(errors)
    .map((error) => error?.message)
    .filter(Boolean)
    .map(String)
    .join(", ");

  // With a blockers strip present the form already says what is unmet, so the button
  // stays clickable and handleSubmit does the revealing. Without one, disabling here
  // is the only guard — and the reason an invalid form can look inert (DEV-2995).
  const disabled =
    submitIsDisabled ||
    (control && !hasBlockers && (!isValid || !errorsIsEmpty));

  return (
    <SubmitButtonBase
      {...props}
      disabled={disabled}
      tooltipTitle={errorMessages || props.submitTooltip}
    />
  );
}

export default function SubmitButton(props: Readonly<SubmitButtonProps>) {
  if (props.control) {
    return <FormStateSubmitButton {...props} />;
  }

  return (
    <SubmitButtonBase
      {...props}
      disabled={props.submitIsDisabled}
      tooltipTitle={props.submitTooltip}
    />
  );
}
